import { z } from 'zod';

/*
 * Personal data protection (Ley para la Protección de Datos Personales de El Salvador,
 * reform of 17/09/2026 as described to us: consents with legal basis and purpose,
 * revocation applied within 5 business days, a versioned privacy notice that states
 * how to file ARCO-POL requests, and contact statuses for rectification, limitation
 * and opposition to marketing). Texts shown to users are Spanish.
 */

export const LEGAL_BASES = {
  consent: 'Consentimiento',
  contract: 'Contrato o gestiones previas',
  legal_obligation: 'Obligación legal',
  legitimate_interest: 'Interés legítimo',
  prior_relationship: 'Relación comercial previa',
} as const;
export type LegalBasis = keyof typeof LEGAL_BASES;

/** What the data is used for (each consent covers one purpose). */
export const CONSENT_PURPOSES = {
  commercial: {
    label: 'Gestión comercial',
    help: 'Atender su búsqueda o su propiedad: contactarle, agendar visitas y enviarle propuestas.',
  },
  marketing: {
    label: 'Marketing',
    help: 'Enviarle promociones y novedades que no pidió expresamente.',
  },
  network: {
    label: 'Compartir con inmobiliarias colaboradoras',
    help: 'Compartir datos necesarios con otras inmobiliarias de la Red para cerrar una operación.',
  },
} as const;
export type ConsentPurpose = keyof typeof CONSENT_PURPOSES;

export const CONSENT_SOURCES = {
  web_form: 'Formulario del sitio web',
  verbal: 'Verbal (llamada o visita)',
  document: 'Documento firmado',
  import: 'Importación',
  api: 'Integración (API)',
} as const;
export type ConsentSource = keyof typeof CONSENT_SOURCES;

/** Business days to apply a revocation outside the system (see business_days_add). */
export const REVOCATION_BUSINESS_DAYS = 5;

const note = z.string().trim().max(500, 'Máximo 500 caracteres');

export const privacyNoticeSchema = z
  .strictObject({
    kind: z.enum(['text', 'url']),
    body: z.string().trim().max(20_000).optional(),
    url: z.url('Enlace no válido').max(500).optional(),
    /** Mandatory: how to file ARCO-POL requests (email, address, form...). */
    arcoChannels: z
      .string()
      .trim()
      .min(10, 'Indica cómo presentar solicitudes (correo, dirección, formulario)')
      .max(1000),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'text' && !v.body) {
      ctx.addIssue({ code: 'custom', path: ['body'], message: 'Escribe el texto del aviso' });
    }
    if (v.kind === 'url' && !v.url) {
      ctx.addIssue({ code: 'custom', path: ['url'], message: 'Escribe el enlace del aviso' });
    }
    if (v.url && !v.url.startsWith('https://')) {
      ctx.addIssue({
        code: 'custom',
        path: ['url'],
        message: 'El enlace debe empezar con https://',
      });
    }
  });
export type PrivacyNoticeInput = z.infer<typeof privacyNoticeSchema>;

export const consentSchema = z.strictObject({
  purpose: z.enum(Object.keys(CONSENT_PURPOSES) as [ConsentPurpose]),
  legalBasis: z.enum(Object.keys(LEGAL_BASES) as [LegalBasis]),
  source: z.enum(Object.keys(CONSENT_SOURCES) as [ConsentSource]),
  /** Where it can be checked: "Formulario de contacto, 27/09/2026", "Contrato de corretaje #12". */
  evidence: note.optional(),
  /** When it was given (defaults to now; cannot be in the future). */
  grantedAt: z.iso.datetime({ offset: true }).optional(),
});
export type ConsentInput = z.infer<typeof consentSchema>;

export const revokeConsentSchema = z.strictObject({
  source: z.enum(Object.keys(CONSENT_SOURCES) as [ConsentSource]),
  note: note.optional(),
});
export type RevokeConsentInput = z.infer<typeof revokeConsentSchema>;

/** Contact statuses; each change needs a short reason when it is switched on. */
export const privacyStatusSchema = z
  .strictObject({
    rectification: z.boolean().optional(),
    rectificationNote: note.optional(),
    processingLimited: z.boolean().optional(),
    limitationNote: note.optional(),
    marketingOptOut: z.boolean().optional(),
    /** The privacy notice was given to an imported contact (clears "pendiente"). */
    noticeDelivered: z.literal(true).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'No hay cambios')
  .refine((v) => v.rectification !== true || Boolean(v.rectificationNote), {
    path: ['rectificationNote'],
    message: 'Indica qué dato se está corrigiendo',
  })
  .refine((v) => v.processingLimited !== true || Boolean(v.limitationNote), {
    path: ['limitationNote'],
    message: 'Indica el motivo de la limitación',
  });
export type PrivacyStatusInput = z.infer<typeof privacyStatusSchema>;

export const tenantHolidaySchema = z.strictObject({
  date: z.iso.date('Fecha no válida'),
  name: z.string().trim().min(1, 'Escribe el motivo').max(100),
});
export type TenantHolidayInput = z.infer<typeof tenantHolidaySchema>;

// ---------------------------------------------------------------------------
// Data subjects' requests (ARCO-POL), per the reform of 17/09/2026.
// ---------------------------------------------------------------------------

/** Business days to answer; one extension adds as many (with a justification). */
export const REQUEST_RESPONSE_DAYS = 20;
export const REQUEST_EXTENSION_DAYS = 20;
/** Business days the person has to complete what a prevention asks for. */
export const REQUEST_PREVENTION_DAYS = 10;
/** Privacy managers are warned this many business days before a request is due. */
export const REQUEST_WARNING_DAYS = 3;

/** The seven rights (ARCO-POL). */
export const PRIVACY_RIGHTS = {
  access: {
    label: 'Acceso',
    help: 'Saber qué datos suyos tenemos, para qué y de dónde vienen.',
  },
  rectification: {
    label: 'Rectificación',
    help: 'Corregir o completar datos inexactos. Mientras se atiende, el contacto queda «en proceso de rectificación».',
  },
  cancellation: {
    label: 'Cancelación',
    help: 'Que se eliminen sus datos cuando ya no son necesarios.',
  },
  opposition: {
    label: 'Oposición',
    help: 'Que dejemos de usar sus datos para algo (por ejemplo, marketing).',
  },
  portability: {
    label: 'Portabilidad',
    help: 'Recibir sus datos en un formato estructurado para llevarlos a otro lugar.',
  },
  erasure: {
    label: 'Olvido',
    help: 'Que se eliminen sus datos y los enlaces públicos relacionados con la persona.',
  },
  limitation: {
    label: 'Limitación',
    help: 'Que sus datos solo se conserven, sin usarlos, mientras se resuelve algo.',
  },
} as const;
export type PrivacyRight = keyof typeof PRIVACY_RIGHTS;

export const REQUEST_CHANNELS = {
  email: 'Correo electrónico',
  phone: 'Teléfono',
  in_person: 'En persona',
  web: 'Sitio web',
  letter: 'Carta o documento',
  other: 'Otro',
} as const;
export type RequestChannel = keyof typeof REQUEST_CHANNELS;

export const REQUEST_STATUSES = {
  received: 'Recibida',
  in_progress: 'En trámite',
  in_prevention: 'En prevención',
  resolved: 'Resuelta',
  withdrawn: 'Desistida',
  incomplete: 'No completada',
} as const;
export type RequestStatus = keyof typeof REQUEST_STATUSES;

export const REQUEST_OUTCOMES = {
  granted: 'Procede',
  partially_granted: 'Procede en parte',
  partially_retained: 'Procede en parte por obligación legal de conservar',
  denied: 'No procede',
} as const;
export type RequestOutcome = keyof typeof REQUEST_OUTCOMES;

export const IDENTITY_METHODS = {
  id_in_person: 'DUI o pasaporte presentado en persona',
  id_copy: 'Copia del documento de identidad',
  known_email: 'Escribió desde el correo que tenemos registrado',
  known_phone: 'Llamó desde el teléfono que tenemos registrado',
  other: 'Otro medio fiable',
} as const;
export type IdentityMethod = keyof typeof IDENTITY_METHODS;

const requestText = (max: number, message: string) =>
  z
    .string()
    .trim()
    .min(1, message)
    .max(max, `Máximo ${String(max)} caracteres`);

/** Anyone registers the reception: the term starts from it. */
export const privacyRequestSchema = z.strictObject({
  right: z.enum(Object.keys(PRIVACY_RIGHTS) as [PrivacyRight], { message: 'Elige el derecho' }),
  requesterName: requestText(200, 'Escribe quién lo pide'),
  replyTo: z.string().trim().max(300).optional(),
  channel: z.enum(Object.keys(REQUEST_CHANNELS) as [RequestChannel], {
    message: 'Elige por dónde llegó',
  }),
  description: requestText(5000, 'Describe lo que pide'),
  /** When it arrived (default: now); never in the future. */
  receivedAt: z.iso.datetime({ offset: true }).optional(),
  contactId: z.uuid().nullable().default(null),
});
export type PrivacyRequestInput = z.input<typeof privacyRequestSchema>;

export const requestPreventionSchema = z.strictObject({
  note: requestText(2000, 'Indica qué falta'),
});
export const requestExtensionSchema = z.strictObject({
  reason: z
    .string()
    .trim()
    .min(10, 'Justifica la prórroga (mínimo 10 caracteres)')
    .max(500, 'Máximo 500 caracteres'),
});
export const requestIdentitySchema = z.strictObject({
  method: z.enum(Object.keys(IDENTITY_METHODS) as [IdentityMethod], {
    message: 'Elige cómo verificaste la identidad',
  }),
});
export const requestResolutionSchema = z
  .strictObject({
    outcome: z.enum(Object.keys(REQUEST_OUTCOMES) as [RequestOutcome], {
      message: 'Elige la resolución',
    }),
    response: requestText(5000, 'Escribe la respuesta que se envió'),
    retentionReason: z.string().trim().max(500).optional(),
    /** The agency holds nothing about the person: needs a prior search in the system. */
    noData: z.boolean().optional(),
  })
  .refine((v) => v.outcome !== 'partially_retained' || (v.retentionReason ?? '').length >= 5, {
    path: ['retentionReason'],
    message: 'Indica qué obligación legal exige conservar los datos',
  });
export const requestCloseSchema = z.strictObject({
  note: z.string().trim().max(500).optional(),
});
export const requestLinkSchema = z.strictObject({ contactId: z.uuid().nullable() });

/** What a request's search in the system looks for (the values are not kept). */
export const REQUEST_SEARCH_CRITERIA = {
  name: 'nombre',
  phone: 'teléfono',
  email: 'correo',
  nationalId: 'DUI',
} as const;
export type RequestSearchCriterion = keyof typeof REQUEST_SEARCH_CRITERIA;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const requestSearchSchema = z.strictObject({
  name: z.string().trim().min(2, 'Escribe el nombre de la persona').max(150),
  phone: optionalText(30),
  email: optionalText(254),
  nationalId: optionalText(20),
});
export type RequestSearchInput = z.output<typeof requestSearchSchema>;

export const requestListQuerySchema = z.strictObject({
  status: z.enum(['open', ...(Object.keys(REQUEST_STATUSES) as RequestStatus[])]).optional(),
  right: z.enum(Object.keys(PRIVACY_RIGHTS) as [PrivacyRight]).optional(),
  page: z.coerce.number().int().min(1).max(1000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export const privacySettingsSchema = z.strictObject({
  preventionPausesTerm: z.boolean(),
});

/** Export everything about a contact (access, portability): needs a 2FA step-up. */
export const contactExportSchema = z.strictObject({ requestId: z.uuid().optional() });

/** Anonymize a contact (cancellation, erasure): irreversible, confirmed by typing the name. */
export const anonymizeSchema = z
  .strictObject({
    confirmName: z.string().trim().min(1, 'Escribe el nombre del contacto para confirmar'),
    mode: z.enum(['full', 'retain']).default('full'),
    retentionReason: z.string().trim().max(500).optional(),
    /** Erasure ("olvido"): also revoke the public links related to the person. */
    forget: z.boolean().default(false),
    requestId: z.uuid().optional(),
  })
  .refine((v) => v.mode !== 'retain' || (v.retentionReason ?? '').length >= 5, {
    path: ['retentionReason'],
    message: 'Indica qué obligación legal exige conservar los datos',
  });
export type AnonymizeInput = z.input<typeof anonymizeSchema>;

/** After anonymizing a co-buyer or referrer: a note of a shared deal, replaced or kept. */
export const anonymizationReviewSchema = z.strictObject({
  kind: z.enum(['deal_note', 'activity']),
  itemId: z.uuid(),
  action: z.enum(['redact', 'keep']),
});
